import { useState, useEffect } from 'react';
import {
  Save,
  GripVertical,
  ChevronDown,
  ChevronRight,
  Check,
  RefreshCw,
  Server,
  Database,
  Cpu,
  ArrowUp,
  ArrowDown,
  CheckCircle,
  AlertCircle,
  Plus,
  X,
  Key,
  Shuffle,
} from 'lucide-react';
import { settings, Settings as SettingsType, providersApi, Provider, providerKeys, ProviderKeyStatus, FallbackItem, OpenRouterPreferences } from '../api/client';
import { cn } from '../lib/utils';

function errorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

export default function Settings() {
  const [config, setConfig] = useState<SettingsType | null>(null);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [expandedProviders, setExpandedProviders] = useState<Record<string, boolean>>({});
  const [newModelInput, setNewModelInput] = useState<Record<string, string>>({});
  const [customModels, setCustomModels] = useState<Record<string, string[]>>({});
  const [providerKeyStatus, setProviderKeyStatus] = useState<ProviderKeyStatus | null>(null);
  const [providerKeyInputs, setProviderKeyInputs] = useState<Record<string, string>>({
    openai: '',
    openrouter: '',
    nanogpt: '',
  });

  // OpenRouter preferences state — comma-separated strings for array fields
  const [orSort, setOrSort] = useState<string>('');
  const [orOrder, setOrOrder] = useState('');
  const [orOnly, setOrOnly] = useState('');
  const [orIgnore, setOrIgnore] = useState('');
  const [orQuantizations, setOrQuantizations] = useState<string[]>([]);
  const [orDataCollection, setOrDataCollection] = useState<string>('allow');
  const [orAllowFallbacks, setOrAllowFallbacks] = useState(true);
  const [orRequireParams, setOrRequireParams] = useState(false);
  const [orMaxPricePrompt, setOrMaxPricePrompt] = useState('');
  const [orMaxPriceCompletion, setOrMaxPriceCompletion] = useState('');
  const [orMinThroughput, setOrMinThroughput] = useState('');
  const [orMaxLatency, setOrMaxLatency] = useState('');
  const [orMiddleOut, setOrMiddleOut] = useState(false);

  // Build OpenRouter preferences object from UI state (omit empty/default values)
  const buildOpenRouterPreferences = (): OpenRouterPreferences => {
    const prefs: OpenRouterPreferences = {};

    if (orSort) prefs.sort = orSort as OpenRouterPreferences['sort'];

    const parseCSV = (s: string) => s.split(',').map(v => v.trim()).filter(Boolean);
    if (orOrder.trim()) prefs.order = parseCSV(orOrder);
    if (orOnly.trim()) prefs.only = parseCSV(orOnly);
    if (orIgnore.trim()) prefs.ignore = parseCSV(orIgnore);
    if (orQuantizations.length > 0) prefs.quantizations = orQuantizations;
    if (orDataCollection !== 'allow') prefs.data_collection = orDataCollection as 'allow' | 'deny';
    if (!orAllowFallbacks) prefs.allow_fallbacks = false;
    if (orRequireParams) prefs.require_parameters = true;

    const promptPrice = parseFloat(orMaxPricePrompt);
    const completionPrice = parseFloat(orMaxPriceCompletion);
    if (!isNaN(promptPrice) || !isNaN(completionPrice)) {
      prefs.max_price = {};
      if (!isNaN(promptPrice)) prefs.max_price.prompt = promptPrice;
      if (!isNaN(completionPrice)) prefs.max_price.completion = completionPrice;
    }

    const throughput = parseFloat(orMinThroughput);
    if (!isNaN(throughput)) prefs.preferred_min_throughput = throughput;

    const latency = parseFloat(orMaxLatency);
    if (!isNaN(latency)) prefs.preferred_max_latency = latency;

    if (orMiddleOut) prefs.transforms = ['middle-out'];

    return prefs;
  };

  // Restore OpenRouter preferences state from loaded settings
  const restoreOpenRouterPreferences = (prefs: OpenRouterPreferences) => {
    setOrSort(prefs.sort || '');
    setOrOrder(prefs.order?.join(', ') || '');
    setOrOnly(prefs.only?.join(', ') || '');
    setOrIgnore(prefs.ignore?.join(', ') || '');
    setOrQuantizations(prefs.quantizations || []);
    setOrDataCollection(prefs.data_collection || 'allow');
    setOrAllowFallbacks(prefs.allow_fallbacks !== false);
    setOrRequireParams(prefs.require_parameters === true);
    setOrMaxPricePrompt(prefs.max_price?.prompt?.toString() || '');
    setOrMaxPriceCompletion(prefs.max_price?.completion?.toString() || '');
    setOrMinThroughput(prefs.preferred_min_throughput?.toString() || '');
    setOrMaxLatency(prefs.preferred_max_latency?.toString() || '');
    setOrMiddleOut(prefs.transforms?.includes('middle-out') || false);
  };

  const QUANTIZATION_OPTIONS = ['int4', 'int8', 'fp6', 'fp8', 'fp16', 'bf16', 'fp32'];

  const toggleQuantization = (q: string) => {
    setOrQuantizations(prev =>
      prev.includes(q) ? prev.filter(v => v !== q) : [...prev, q]
    );
  };

  // Normalize fallback chain to new format (handle backwards compatibility)
  const normalizeFallbackChain = (chain: unknown[]): FallbackItem[] => {
    return chain.map(item => {
      if (typeof item === 'string') {
        // Old format: just provider name
        return { provider: item };
      }
      // New format: { provider, model? }
      return item as FallbackItem;
    });
  };

  const fetchData = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [settingsRes, providersRes, keyStatusRes] = await Promise.all([
        settings.get(),
        providersApi.list(),
        providerKeys.getStatus(),
      ]);

      // Normalize fallback chain to new format
      const normalizedSettings = {
        ...settingsRes.settings,
        fallback_chain: normalizeFallbackChain(settingsRes.settings.fallback_chain as unknown[]),
      };

      setConfig(normalizedSettings);
      setProviders(providersRes.providers);
      setProviderKeyStatus(keyStatusRes.status);

      // Restore OpenRouter preferences
      if (settingsRes.settings.openrouter_preferences) {
        restoreOpenRouterPreferences(settingsRes.settings.openrouter_preferences as OpenRouterPreferences);
      }

      // Initialize enabled_models if not set
      if (!settingsRes.settings.enabled_models) {
        const enabledModels: Record<string, string[]> = {};
        providersRes.providers.forEach((p: Provider) => {
          enabledModels[p.id] = p.enabledModels;
        });
        setConfig(prev => prev ? { ...prev, enabled_models: enabledModels } : null);
      }

      // Restore custom models from enabledModels (models in enabledModels but not in built-in models)
      const restoredCustomModels: Record<string, string[]> = {};
      providersRes.providers.forEach((p: Provider) => {
        const enabledForProvider = settingsRes.settings.enabled_models?.[p.id] || p.enabledModels;
        const builtInModels = p.models;
        const custom = enabledForProvider.filter((m: string) => !builtInModels.includes(m));
        if (custom.length > 0) {
          restoredCustomModels[p.id] = custom;
        }
      });
      setCustomModels(restoredCustomModels);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      setLoadError(errorMessage(error, 'Failed to load settings.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSave = async () => {
    if (!config) return;

    setSaving(true);
    setSaveError(null);
    try {
      // Build the update payload - include provider keys only if entered
      const updatePayload: Record<string, unknown> = { ...config };

      // Include OpenRouter preferences
      updatePayload.openrouter_preferences = buildOpenRouterPreferences();

      // Add provider keys if user has entered new values
      if (providerKeyInputs.openai.trim()) {
        updatePayload.provider_key_openai = providerKeyInputs.openai.trim();
      }
      if (providerKeyInputs.openrouter.trim()) {
        updatePayload.provider_key_openrouter = providerKeyInputs.openrouter.trim();
      }
      if (providerKeyInputs.nanogpt.trim()) {
        updatePayload.provider_key_nanogpt = providerKeyInputs.nanogpt.trim();
      }

      await settings.update(updatePayload);

      // Clear the key inputs after save
      setProviderKeyInputs({ openai: '', openrouter: '', nanogpt: '' });

      // Refresh the key status
      try {
        const keyStatusRes = await providerKeys.getStatus();
        setProviderKeyStatus(keyStatusRes.status);
      } catch (error) {
        console.error('Failed to refresh provider key status:', error);
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setSaved(false);
      setSaveError(errorMessage(error, 'Failed to save settings.'));
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (key: keyof SettingsType, value: unknown) => {
    setConfig((prev) => (prev ? { ...prev, [key]: value } : null));
  };

  const moveFallback = (fromIndex: number, toIndex: number) => {
    if (!config) return;

    const chain = [...config.fallback_chain];
    const [removed] = chain.splice(fromIndex, 1);
    chain.splice(toIndex, 0, removed);

    updateConfig('fallback_chain', chain);
  };

  const updateFallbackModel = (index: number, model: string | undefined) => {
    if (!config) return;

    const chain = [...config.fallback_chain];
    chain[index] = { ...chain[index], model: model || undefined };

    updateConfig('fallback_chain', chain);
  };

  // Get available models for a provider (enabled models from settings)
  const getModelsForProvider = (providerId: string): string[] => {
    const provider = providers.find(p => p.id === providerId);
    if (!provider) return [];
    return provider.enabledModels;
  };

  const toggleProviderExpanded = (providerId: string) => {
    setExpandedProviders(prev => ({
      ...prev,
      [providerId]: !prev[providerId]
    }));
  };

  const toggleModel = (providerId: string, model: string) => {
    if (!config) return;

    const currentEnabledModels = config.enabled_models || {};
    const providerModels = currentEnabledModels[providerId] || [];

    let newProviderModels: string[];
    if (providerModels.includes(model)) {
      newProviderModels = providerModels.filter(m => m !== model);
    } else {
      newProviderModels = [...providerModels, model];
    }

    updateConfig('enabled_models', {
      ...currentEnabledModels,
      [providerId]: newProviderModels
    });
  };

  const toggleAllModels = (providerId: string, allModels: string[]) => {
    if (!config) return;

    const currentEnabledModels = config.enabled_models || {};
    const providerModels = currentEnabledModels[providerId] || [];

    // If all are selected, deselect all; otherwise select all
    const allSelected = allModels.every(m => providerModels.includes(m));

    updateConfig('enabled_models', {
      ...currentEnabledModels,
      [providerId]: allSelected ? [] : [...allModels]
    });
  };

  const isModelEnabled = (providerId: string, model: string) => {
    if (!config?.enabled_models) return true;
    const providerModels = config.enabled_models[providerId];
    if (!providerModels) return true;
    return providerModels.includes(model);
  };

  const getEnabledCount = (providerId: string, allModels: string[]) => {
    if (!config?.enabled_models) return allModels.length;
    const enabledModels = config.enabled_models[providerId];
    if (!enabledModels) return allModels.length;
    // Only count enabled models that actually exist in the available models list
    return enabledModels.filter(m => allModels.includes(m)).length;
  };

  // Get all models for a provider (built-in + custom)
  const getAllModelsForProvider = (provider: Provider): string[] => {
    const builtIn = provider.models;
    const custom = customModels[provider.id] || [];
    return [...builtIn, ...custom.filter(m => !builtIn.includes(m))];
  };

  // Add a custom model to a provider
  const addCustomModel = (providerId: string) => {
    const modelName = newModelInput[providerId]?.trim();
    if (!modelName) return;

    const provider = providers.find(p => p.id === providerId);
    const existingModels = provider?.models || [];
    const existingCustom = customModels[providerId] || [];

    // Don't add if already exists
    if (existingModels.includes(modelName) || existingCustom.includes(modelName)) {
      return;
    }

    // Add to custom models
    setCustomModels(prev => ({
      ...prev,
      [providerId]: [...(prev[providerId] || []), modelName]
    }));

    // Also enable it by default
    if (config) {
      const currentEnabledModels = config.enabled_models || {};
      const providerModels = currentEnabledModels[providerId] || [];
      updateConfig('enabled_models', {
        ...currentEnabledModels,
        [providerId]: [...providerModels, modelName]
      });
    }

    // Clear the input
    setNewModelInput(prev => ({ ...prev, [providerId]: '' }));
  };

  // Remove a custom model
  const removeCustomModel = (providerId: string, modelName: string) => {
    setCustomModels(prev => ({
      ...prev,
      [providerId]: (prev[providerId] || []).filter(m => m !== modelName)
    }));

    // Also remove from enabled models
    if (config) {
      const currentEnabledModels = config.enabled_models || {};
      const providerModels = currentEnabledModels[providerId] || [];
      updateConfig('enabled_models', {
        ...currentEnabledModels,
        [providerId]: providerModels.filter(m => m !== modelName)
      });
    }
  };

  // Check if a model is a custom model (not built-in)
  const isCustomModel = (providerId: string, modelName: string): boolean => {
    const provider = providers.find(p => p.id === providerId);
    return !(provider?.models || []).includes(modelName);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading settings...</span>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="card-glow rounded-2xl p-6 max-w-md w-full text-center animate-fade-in-up">
          <div className="w-12 h-12 rounded-xl bg-rose-500/10 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6 text-rose-400" />
          </div>
          <h2 className="text-lg font-semibold mb-2">Settings unavailable</h2>
          <p className="text-sm text-muted-foreground mb-5">
            {loadError || 'Failed to load settings.'}
          </p>
          <button
            onClick={fetchData}
            className="btn-secondary mx-auto px-4 py-2.5 rounded-xl flex items-center gap-2 text-sm"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold text-gradient">Settings</h1>
          <p className="text-muted-foreground mt-1">Configure your AI proxy system</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className={cn(
            'px-5 py-2.5 rounded-xl flex items-center gap-2 text-sm transition-all',
            saved
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
              : 'btn-glow'
          )}
        >
          <span className="flex items-center gap-2">
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : saved ? (
              <>
                <CheckCircle className="w-4 h-4" />
                Saved!
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </span>
        </button>
      </div>

      {saveError && (
        <div className="bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded-xl px-4 py-3 text-sm flex items-start gap-3 animate-scale-in">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium">Failed to save settings</p>
            <p className="text-rose-300/80 mt-0.5">{saveError}</p>
          </div>
          <button
            onClick={() => setSaveError(null)}
            className="p-1 rounded-lg hover:bg-rose-500/10 transition-colors"
            aria-label="Dismiss save error"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Provider API Keys */}
      <div className="card-glow rounded-2xl p-6 animate-fade-in-up animate-delay-100">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-rose-500/10 flex items-center justify-center">
            <Key className="w-5 h-5 text-rose-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Provider API Keys</h2>
            <p className="text-sm text-muted-foreground">Configure API keys for each provider</p>
          </div>
        </div>

        <div className="space-y-4">
          {[
            { id: 'openai', name: 'OpenAI', placeholder: 'sk-...' },
            { id: 'openrouter', name: 'OpenRouter', placeholder: 'sk-or-...' },
            { id: 'nanogpt', name: 'NanoGPT', placeholder: 'Enter API key' },
          ].map((provider) => {
            const isConfigured = providerKeyStatus?.[provider.id as keyof ProviderKeyStatus] ?? false;
            return (
              <div key={provider.id} className="flex items-center gap-4 p-4 bg-secondary rounded-xl border border-border">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-medium">{provider.name}</span>
                    {isConfigured ? (
                      <span className="flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-full">
                        <CheckCircle className="w-3 h-3" />
                        Configured
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground bg-background px-2 py-1 rounded-full">
                        Not configured
                      </span>
                    )}
                  </div>
                  <input
                    type="password"
                    value={providerKeyInputs[provider.id] || ''}
                    onChange={(e) => setProviderKeyInputs(prev => ({ ...prev, [provider.id]: e.target.value }))}
                    placeholder={isConfigured ? 'Enter new key to update' : provider.placeholder}
                    className="input-neural w-full px-4 py-2.5 rounded-xl text-sm font-mono"
                  />
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-4 text-xs text-muted-foreground">
          API keys are stored securely and never displayed after saving. Leave a field empty to keep the existing key.
        </p>
      </div>

      {/* Provider Settings */}
      <div className="card-glow rounded-2xl p-6 animate-fade-in-up animate-delay-200">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
            <Server className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Provider Settings</h2>
            <p className="text-sm text-muted-foreground">Configure AI provider preferences</p>
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Default Provider
            </label>
            <select
              value={config.default_provider}
              onChange={(e) => updateConfig('default_provider', e.target.value)}
              className="input-neural w-full px-4 py-3 rounded-xl text-sm appearance-none cursor-pointer"
            >
              {providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs text-muted-foreground">
              The default provider to use when not specified in the request.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-3">
              Fallback Chain
            </label>
            <div className="space-y-2">
              {config.fallback_chain.map((fallbackItem, index) => {
                const provider = providers.find((p) => p.id === fallbackItem.provider);
                const availableModels = getModelsForProvider(fallbackItem.provider);
                return (
                  <div
                    key={`${fallbackItem.provider}-${index}`}
                    className="flex items-center gap-3 p-4 bg-secondary rounded-xl border border-border"
                  >
                    <GripVertical className="w-4 h-4 text-muted-foreground cursor-move flex-shrink-0" />
                    <div className="w-7 h-7 rounded-lg bg-violet-500/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-mono text-violet-400">{index + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium mb-2">
                        {provider?.name || fallbackItem.provider}
                      </div>
                      <select
                        value={fallbackItem.model || ''}
                        onChange={(e) => updateFallbackModel(index, e.target.value || undefined)}
                        className="input-neural w-full px-3 py-2 rounded-lg text-sm appearance-none cursor-pointer"
                      >
                        <option value="">Use original request model</option>
                        {availableModels.map((model) => (
                          <option key={model} value={model}>
                            {model}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => moveFallback(index, Math.max(0, index - 1))}
                        disabled={index === 0}
                        className="p-2 rounded-lg bg-background text-muted-foreground hover:text-cyan-400 disabled:opacity-30 disabled:hover:text-muted-foreground transition-colors"
                      >
                        <ArrowUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() =>
                          moveFallback(index, Math.min(config.fallback_chain.length - 1, index + 1))
                        }
                        disabled={index === config.fallback_chain.length - 1}
                        className="p-2 rounded-lg bg-background text-muted-foreground hover:text-cyan-400 disabled:opacity-30 disabled:hover:text-muted-foreground transition-colors"
                      >
                        <ArrowDown className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Order in which providers are tried when one fails. Optionally specify a model to use for each fallback.
            </p>
          </div>
        </div>
      </div>

      {/* OpenRouter Settings */}
      <div className="card-glow rounded-2xl p-6 animate-fade-in-up animate-delay-250">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
            <Shuffle className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">OpenRouter Settings</h2>
            <p className="text-sm text-muted-foreground">Provider routing, sorting, and cost controls for OpenRouter</p>
          </div>
        </div>

        <div className="space-y-5">
          {/* Sort By */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Sort Providers By
            </label>
            <select
              value={orSort}
              onChange={(e) => setOrSort(e.target.value)}
              className="input-neural w-full px-4 py-3 rounded-xl text-sm appearance-none cursor-pointer"
            >
              <option value="">Default (OpenRouter decides)</option>
              <option value="price">Price (cheapest first)</option>
              <option value="throughput">Throughput (fastest tokens/sec)</option>
              <option value="latency">Latency (lowest time-to-first-token)</option>
            </select>
            <p className="mt-2 text-xs text-muted-foreground">
              How OpenRouter ranks providers for your chosen model. Equivalent to <code className="text-amber-400/70">:floor</code>, <code className="text-amber-400/70">:nitro</code> model suffixes.
            </p>
          </div>

          {/* Provider Order */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Provider Order
            </label>
            <input
              type="text"
              value={orOrder}
              onChange={(e) => setOrOrder(e.target.value)}
              placeholder="e.g., anthropic, together, openai"
              className="input-neural w-full px-4 py-3 rounded-xl text-sm font-mono"
            />
            <p className="mt-2 text-xs text-muted-foreground">
              Comma-separated list of provider slugs to try in order.
            </p>
          </div>

          {/* Allow Only / Ignore */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Allow Only
              </label>
              <input
                type="text"
                value={orOnly}
                onChange={(e) => setOrOnly(e.target.value)}
                placeholder="e.g., azure, openai"
                className="input-neural w-full px-4 py-3 rounded-xl text-sm font-mono"
              />
              <p className="mt-2 text-xs text-muted-foreground">
                Whitelist — only route to these providers.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Ignore Providers
              </label>
              <input
                type="text"
                value={orIgnore}
                onChange={(e) => setOrIgnore(e.target.value)}
                placeholder="e.g., deepinfra"
                className="input-neural w-full px-4 py-3 rounded-xl text-sm font-mono"
              />
              <p className="mt-2 text-xs text-muted-foreground">
                Blacklist — never route to these providers.
              </p>
            </div>
          </div>

          {/* Quantizations */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Quantization Preferences
            </label>
            <div className="flex flex-wrap gap-2">
              {QUANTIZATION_OPTIONS.map((q) => (
                <button
                  key={q}
                  onClick={() => toggleQuantization(q)}
                  className={cn(
                    'px-3 py-1.5 rounded-lg text-sm font-mono transition-all border',
                    orQuantizations.includes(q)
                      ? 'bg-amber-500/15 border-amber-500/30 text-amber-400'
                      : 'bg-background border-border text-muted-foreground hover:border-border/80'
                  )}
                >
                  {q}
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Filter providers by model precision level. Leave empty for no preference.
            </p>
          </div>

          {/* Toggles Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-4 bg-secondary rounded-xl border border-border">
              <div>
                <div className="text-sm font-medium">Allow Fallbacks</div>
                <div className="text-xs text-muted-foreground mt-0.5">Use backup providers when primary is down</div>
              </div>
              <button
                onClick={() => setOrAllowFallbacks(!orAllowFallbacks)}
                className={cn(
                  'w-11 h-6 rounded-full transition-colors relative',
                  orAllowFallbacks ? 'bg-amber-500' : 'bg-background border border-border'
                )}
              >
                <div className={cn(
                  'w-4 h-4 rounded-full bg-white absolute top-1 transition-all',
                  orAllowFallbacks ? 'left-6' : 'left-1'
                )} />
              </button>
            </div>
            <div className="flex items-center justify-between p-4 bg-secondary rounded-xl border border-border">
              <div>
                <div className="text-sm font-medium">Require Parameters</div>
                <div className="text-xs text-muted-foreground mt-0.5">Only use providers supporting all params</div>
              </div>
              <button
                onClick={() => setOrRequireParams(!orRequireParams)}
                className={cn(
                  'w-11 h-6 rounded-full transition-colors relative',
                  orRequireParams ? 'bg-amber-500' : 'bg-background border border-border'
                )}
              >
                <div className={cn(
                  'w-4 h-4 rounded-full bg-white absolute top-1 transition-all',
                  orRequireParams ? 'left-6' : 'left-1'
                )} />
              </button>
            </div>
          </div>

          {/* Data Collection */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Data Collection
            </label>
            <select
              value={orDataCollection}
              onChange={(e) => setOrDataCollection(e.target.value)}
              className="input-neural w-full px-4 py-3 rounded-xl text-sm appearance-none cursor-pointer"
            >
              <option value="allow">Allow (default)</option>
              <option value="deny">Deny (zero-data-retention providers only)</option>
            </select>
            <p className="mt-2 text-xs text-muted-foreground">
              Setting to "Deny" restricts routing to providers with zero-data-retention policies.
            </p>
          </div>

          {/* Price & Performance */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Max Price — Prompt ($/M tokens)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={orMaxPricePrompt}
                onChange={(e) => setOrMaxPricePrompt(e.target.value)}
                placeholder="No limit"
                className="input-neural w-full px-4 py-3 rounded-xl text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Max Price — Completion ($/M tokens)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={orMaxPriceCompletion}
                onChange={(e) => setOrMaxPriceCompletion(e.target.value)}
                placeholder="No limit"
                className="input-neural w-full px-4 py-3 rounded-xl text-sm font-mono"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Min Throughput (tokens/sec)
              </label>
              <input
                type="number"
                step="1"
                min="0"
                value={orMinThroughput}
                onChange={(e) => setOrMinThroughput(e.target.value)}
                placeholder="No preference"
                className="input-neural w-full px-4 py-3 rounded-xl text-sm font-mono"
              />
              <p className="mt-2 text-xs text-muted-foreground">
                Prefer providers with at least this throughput.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Max Latency (seconds)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={orMaxLatency}
                onChange={(e) => setOrMaxLatency(e.target.value)}
                placeholder="No preference"
                className="input-neural w-full px-4 py-3 rounded-xl text-sm font-mono"
              />
              <p className="mt-2 text-xs text-muted-foreground">
                Prefer providers with latency below this threshold.
              </p>
            </div>
          </div>

          {/* Transforms */}
          <div className="flex items-center justify-between p-4 bg-secondary rounded-xl border border-border">
            <div>
              <div className="text-sm font-medium">Middle-Out Compression</div>
              <div className="text-xs text-muted-foreground mt-0.5">Automatically truncate prompts exceeding context window (removes middle messages)</div>
            </div>
            <button
              onClick={() => setOrMiddleOut(!orMiddleOut)}
              className={cn(
                'w-11 h-6 rounded-full transition-colors relative',
                orMiddleOut ? 'bg-amber-500' : 'bg-background border border-border'
              )}
            >
              <div className={cn(
                'w-4 h-4 rounded-full bg-white absolute top-1 transition-all',
                orMiddleOut ? 'left-6' : 'left-1'
              )} />
            </button>
          </div>
        </div>
      </div>

      {/* Models per Provider */}
      <div className="card-glow rounded-2xl p-6 animate-fade-in-up animate-delay-300">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center">
            <Cpu className="w-5 h-5 text-violet-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Models by Provider</h2>
            <p className="text-sm text-muted-foreground">Select which models are available for each provider</p>
          </div>
        </div>

        <div className="space-y-3">
          {providers.map((provider) => (
            <div key={provider.id} className="bg-secondary border border-border rounded-xl overflow-hidden">
              {/* Provider Header */}
              <button
                onClick={() => toggleProviderExpanded(provider.id)}
                className="w-full flex items-center justify-between p-4 hover:bg-background/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-md bg-background flex items-center justify-center">
                    {expandedProviders[provider.id] ? (
                      <ChevronDown className="w-4 h-4 text-cyan-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                  <span className="font-medium">{provider.name}</span>
                </div>
                <span className="text-sm text-muted-foreground">
                  {provider.supportsAllModels ? (
                    <span className="text-amber-400">All models supported</span>
                  ) : (
                    (() => {
                      const allModels = getAllModelsForProvider(provider);
                      return (
                        <>
                          <span className="font-mono text-foreground">{getEnabledCount(provider.id, allModels)}</span>
                          {' / '}
                          <span className="font-mono">{allModels.length}</span> models enabled
                        </>
                      );
                    })()
                  )}
                </span>
              </button>

              {/* Models List */}
              {expandedProviders[provider.id] && (
                <div className="p-4 border-t border-border">
                  {/* Add Custom Model Input */}
                  <div className="mb-4">
                    <label className="block text-sm text-muted-foreground mb-2">
                      Add Custom Model
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newModelInput[provider.id] || ''}
                        onChange={(e) => setNewModelInput(prev => ({ ...prev, [provider.id]: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addCustomModel(provider.id);
                          }
                        }}
                        placeholder={provider.supportsAllModels ? "e.g., claude-3-opus, gpt-4o" : "Enter model name"}
                        className="input-neural flex-1 px-4 py-2.5 rounded-xl text-sm font-mono"
                      />
                      <button
                        onClick={() => addCustomModel(provider.id)}
                        disabled={!newModelInput[provider.id]?.trim()}
                        className={cn(
                          'px-4 py-2.5 rounded-xl flex items-center gap-2 text-sm transition-all',
                          newModelInput[provider.id]?.trim()
                            ? 'btn-glow'
                            : 'bg-secondary text-muted-foreground cursor-not-allowed'
                        )}
                      >
                        <Plus className="w-4 h-4" />
                        Add
                      </button>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      {provider.supportsAllModels
                        ? 'This provider supports any model. Add model names to use with your API keys.'
                        : 'Add additional models beyond the built-in ones.'}
                    </p>
                  </div>

                  {(() => {
                    const allModels = getAllModelsForProvider(provider);

                    if (!provider.supportsAllModels && allModels.length === 0) {
                      return (
                        <p className="text-sm text-muted-foreground italic">
                          No models configured for this provider. Add one above.
                        </p>
                      );
                    }

                    return (
                      <>
                        {/* Select All / Deselect All (only for non-wildcard providers) */}
                        {!provider.supportsAllModels && allModels.length > 0 && (
                          <div className="mb-4 flex items-center justify-between">
                            <button
                              onClick={() => toggleAllModels(provider.id, allModels)}
                              className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
                            >
                              {allModels.every(m => isModelEnabled(provider.id, m))
                                ? 'Deselect All'
                                : 'Select All'}
                            </button>
                          </div>
                        )}

                        {/* Model Checkboxes */}
                        {allModels.length > 0 && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {allModels.map((model) => {
                              const isCustom = isCustomModel(provider.id, model);
                              return (
                                <div
                                  key={model}
                                  className={cn(
                                    'flex items-center p-3 rounded-lg transition-all',
                                    isModelEnabled(provider.id, model)
                                      ? 'bg-cyan-500/10 border border-cyan-500/30'
                                      : 'bg-background border border-border hover:border-border/80'
                                  )}
                                >
                                  <div
                                    className={cn(
                                      'w-5 h-5 rounded border flex items-center justify-center mr-3 transition-colors cursor-pointer',
                                      isModelEnabled(provider.id, model)
                                        ? 'bg-cyan-500 border-cyan-500'
                                        : 'bg-transparent border-muted-foreground/50'
                                    )}
                                    onClick={() => toggleModel(provider.id, model)}
                                  >
                                    {isModelEnabled(provider.id, model) && (
                                      <Check className="w-3 h-3 text-[hsl(222,47%,6%)]" />
                                    )}
                                  </div>
                                  <span className="text-sm font-mono text-foreground/90 flex-1">{model}</span>
                                  {isCustom && (
                                    <button
                                      onClick={() => removeCustomModel(provider.id, model)}
                                      className="p-1.5 rounded-lg text-muted-foreground hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
                                      title="Remove custom model"
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Client Compatibility */}
      <div className="card-glow rounded-2xl p-6 animate-fade-in-up animate-delay-500">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
            <RefreshCw className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Client Compatibility</h2>
            <p className="text-sm text-muted-foreground">Remotely select the safe conversation context format</p>
          </div>
        </div>

        <label className="block text-xs text-muted-foreground mb-2">
          Context Format Override
        </label>
        <select
          value={config.context_format_arm_override ?? ''}
          onChange={(e) => updateConfig(
            'context_format_arm_override',
            e.target.value as '' | 'today'
          )}
          className="input-neural w-full px-4 py-3 rounded-xl text-sm appearance-none cursor-pointer"
        >
          <option value="">Use the app-selected format</option>
          <option value="today">Force the legacy “today” format</option>
        </select>
        <p className="mt-2 text-xs text-muted-foreground">
          “Today” adds X-PLLM-Format-Override: today to chat responses so packet-capable apps fall back remotely.
        </p>
      </div>

      {/* Log Settings */}
      <div className="card-glow rounded-2xl p-6 animate-fade-in-up animate-delay-500">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
            <Database className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Log Settings</h2>
            <p className="text-sm text-muted-foreground">Configure data retention and session behavior</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-xs text-muted-foreground mb-2">
              Log Retention (days)
            </label>
            <input
              type="number"
              value={config.log_retention_days}
              onChange={(e) => { const val = parseInt(e.target.value, 10); updateConfig('log_retention_days', isNaN(val) ? 0 : val); }}
              className="input-neural w-full px-4 py-3 rounded-xl text-sm font-mono"
            />
            <p className="mt-2 text-xs text-muted-foreground">
              Logs older than this will be automatically deleted.
            </p>
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-2">
              Session Timeout (minutes)
            </label>
            <input
              type="number"
              value={config.session_timeout_minutes}
              onChange={(e) => { const val = parseInt(e.target.value, 10); updateConfig('session_timeout_minutes', isNaN(val) ? 0 : val); }}
              className="input-neural w-full px-4 py-3 rounded-xl text-sm font-mono"
            />
            <p className="mt-2 text-xs text-muted-foreground">
              New session created after this idle time.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
